import React, { useMemo, Suspense, lazy, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "../../css/keyword.scss";

import { ZustandStore } from "../../../store/zustand-store";

import FallbackLoader from "../../share/components/FallbackLoader";
import KeywordDataProvider from "./KeywordDataProvider";
import { handleInvitedUserAccess } from "../../../helperJs/helper";
import LastCheck from "../Search-History/LastCheck";
import { useTranslation } from "react-i18next";
import { keyword_overview } from "../../../Navigation/constant";

const LazyKeywordsTable = lazy(() =>
  import("../../share/tables/KeywordsTable")
);

const KeywordIdeas = () => {
  const [keywordName, setKeywordName] = useState("");
  const [domainLocation, setDomainLocation] = useState({
    location_name: "United States",
    location_code: 2840,
    location_flag: "https://flagsapi.com/US/flat/24.png",
    language_code: "en",
    language_name: "English",
  });
  const email = useSelector((state) => state.email);
  const { t } = useTranslation();

  const { keyword_research_ideas_data, KeywordTableColumn, userAccessData } =
    ZustandStore((state) => state);

  const page_data = userAccessData?.[0]?.access;
  const isPageAccess = handleInvitedUserAccess("keywordresearch", page_data);

  const config = useMemo(() => {
    return {
      email,
      proj_name: keywordName,
      tag_type: "keyword",
      location_name: domainLocation.location_name,
      language_name: domainLocation.language_name,
    };
  }, [email, domainLocation, keywordName]);

  const getSessionStorageItem = (key, parseJson = false) => {
    const item = sessionStorage.getItem(key);
    return parseJson && item ? JSON.parse(item) : item;
  };

  const handleTabChange = () => {
    sessionStorage.setItem("FREE_TRIAL_TABLE", "keywordTable");
    ZustandStore.setState({
      tag_keywords: [],
      filter_tag: [],
      keyword_tag_data: {
        loaded: false,
        error: false,
        data: [],
      },
    });
  };

  const domainName = getSessionStorageItem("KEYWORD_DOMAIN_NAME");
  const onRefreshCall = () => {
    const country = getSessionStorageItem("KEYWORD_DOMAIN_COUNTRY", true);

    if (!isPageAccess) {
      sessionStorage.removeItem("KEYWORD_DOMAIN_NAME");
      sessionStorage.removeItem("KEYWORD_DOMAIN_COUNTRY");
      setKeywordName("");
      setDomainLocation({
        location_name: "United States",
        location_code: 2840,
        location_flag: "https://flagsapi.com/US/flat/24.png",
        language_code: "en",
        language_name: "English",
      });
      return;
    }

    if (country) {
      setDomainLocation({
        location_name: country?.location_name,
        location_code: country?.location_code,
        location_flag: country?.location_flag,
        language_code: country?.language_code,
        language_name: country?.language_name,
      });
    }
    if (domainName) {
      setKeywordName(domainName);
    }
    handleTabChange();
  };

  useEffect(() => {
    onRefreshCall();
  }, [domainName]);

  const KeywordHeader = useMemo(() => {
    return [
      {
        id: "keyword",
        sort: "keyword",
        key: "keyword",
        tooltip: t("keyword_research.keyword_variations.table.keyword.tooltip"),
        minWidth: "300px",
        label: t("keyword_research.keyword_variations.table.keyword.title"),
        align: "left",
        isTrending: false,
        isAiWriter: false,
        show: true,
        ignore: true,
      },

      {
        id: "volume",
        key: "volume",
        sort: "volume",
        tooltip: t("keyword_research.keyword_variations.table.volume.tooltip"),
        minWidth: "110px",
        label: t("keyword_research.keyword_variations.table.volume.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: true,
        show: !KeywordTableColumn.includes("Volume"),
      },
      {
        id: "avg_volume",
        key: "avg_volume",
        sort: "avg_volume",
        tooltip: t("keyword_research.keyword_variations.table.avg_vol.tooltip"),
        minWidth: "110px",
        label: t("keyword_research.keyword_variations.table.avg_vol.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Volume"),
      },
      {
        id: "cpc",
        key: "cpc",
        sort: "cpc",
        tooltip: t("keyword_research.keyword_variations.table.cpc.tooltip"),
        minWidth: "70px",
        label: t("keyword_research.keyword_variations.table.cpc.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("CPC"),
      },
      {
        id: "vol_trend",
        key: "vol_trend",
        sort: "vol_trend",
        tooltip: t(
          "keyword_research.keyword_variations.table.vol_trend.tooltip"
        ),
        minWidth: "91px",
        label: t("keyword_research.keyword_variations.table.vol_trend.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Vol Trend"),
      },
      {
        id: "search_intent",
        key: "search_intent",
        sort: "search_intent",
        tooltip: t("keyword_research.keyword_variations.table.intent.tooltip"),
        minWidth: "80px",
        label: t("keyword_research.keyword_variations.table.intent.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Intent"),
      },
      {
        id: "sd",
        key: "difficulty",
        sort: "sd",
        tooltip: t("keyword_research.keyword_variations.table.sd.tooltip"),
        minWidth: "70px",
        label: t("keyword_research.keyword_variations.table.sd.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("SD"),
      },
      {
        id: "backlinks",
        key: "avg_backlinks",
        sort: "backlinks",
        tooltip: t(
          "keyword_research.keyword_variations.table.avg_backlinks.tooltip"
        ),
        minWidth: "110px",
        label: t(
          "keyword_research.keyword_variations.table.avg_backlinks.title"
        ),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Avg Backlinks"),
      },
      {
        id: "reff_domains",
        key: "avg_reff_domains",
        sort: "reff_domains",
        tooltip: t(
          "keyword_research.keyword_variations.table.reff_domains.tooltip"
        ),
        minWidth: "100px",
        label: t(
          "keyword_research.keyword_variations.table.reff_domains.title"
        ),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Avg Ref Domains"),
      },
      {
        id: "click",
        key: "avg_click",
        sort: "click",
        tooltip: t("keyword_research.keyword_variations.table.click.tooltip"),
        minWidth: "90px",
        label: t("keyword_research.keyword_variations.table.click.title"),
        align: "center",
        isTrending: true,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("Avg Clicks"),
      },
      // {
      //   id: "impression",
      //   key: "avg_impression",
      //   sort: "impression",
      //   tooltip: t(
      //     "keyword_research.keyword_variations.table.impression.tooltip"
      //   ),
      //   minWidth: "120px",
      //   label: t("keyword_research.keyword_variations.table.impression.title"),
      //   align: "center",
      //   isTrending: true,
      //   isAiWriter: false,
      //   ignore: false,
      //   show: !KeywordTableColumn.includes("Avg Impressions"),
      // },
      {
        id: "AI",
        key: "AI",
        tooltip: t("keyword_research.keyword_variations.table.ai.tooltip"),
        minWidth: "55px",
        label: t("keyword_research.keyword_variations.table.ai.title"),
        align: "center",
        isTrending: false,
        isAiWriter: false,
        ignore: false,
        show: !KeywordTableColumn.includes("AI"),
      },
    ];
  }, [KeywordTableColumn, t]);

  const keywordRowData = useMemo(() => {
    if (keyword_research_ideas_data?.data?.length > 0) {
      return keyword_research_ideas_data?.data?.map((item) => {
        const avgVolume = item?.keyword_info?.monthly_searches ?? []; // Use nullish coalescing
        const totalVolume = avgVolume.reduce(
          (sum, { search_volume }) => sum + search_volume,
          0
        );
        const averageVolume =
          avgVolume.length > 0 ? Math.round(totalVolume / avgVolume.length) : 0;
        return {
          keyword: item.keyword,
          volume: item?.keyword_info?.monthly_searches[0]?.search_volume || 0,
          avg_volume: averageVolume,
          vol_trend: item?.keyword_info?.search_volume_trend?.quarterly || 0,
          cpc: item?.keyword_info?.cpc || 0,
          sd: item?.keyword_properties?.keyword_difficulty || 0,
          backlinks: item?.avg_backlinks_info?.backlinks || 0,
          reff_domains: item?.avg_backlinks_info?.referring_domains || 0,
          click: item?.impressions_info?.daily_clicks_average || 0,
          impression: item?.impressions_info?.daily_impressions_average || 0,
          search_intent: item?.search_intent_info?.main_intent || 0,
          check_url: item?.serp_info?.check_url || 0,
          competition: item?.keyword_info?.competition || 0,
          keyword_monthly_searches: item?.keyword_info?.monthly_searches,
        };
      });
    } else {
      return [];
    }
  }, [keyword_research_ideas_data]);

  const keywordTableData = useMemo(() => {
    return {
      type: "keyword",
      addToRankTracking: true,
      header_name: { title: "Keyword Ideas", show: false },
      header: KeywordHeader,
      row: keywordRowData,
      config,
    };
  }, [keywordRowData, KeywordHeader, config]);

  return (
    <KeywordDataProvider title={t("keyword_research.keyword_ideas.title")}>
      {keyword_research_ideas_data?.data.length === 0 ? (
        <div className="mt-2">
          <LastCheck type="keyword" />
        </div>
      ) : null}
      {keyword_research_ideas_data?.loading ? (
        <FallbackLoader />
      ) : keyword_research_ideas_data?.search ? (
        <>
          <section className="page-tab-container">
            <Suspense fallback={<FallbackLoader />}>
              <LazyKeywordsTable Data={keywordTableData} />
            </Suspense>
          </section>
        </>
      ) : null}
    </KeywordDataProvider>
  );
};

export default KeywordIdeas;
